/**
 * Job postings, and the funnel underneath each one.
 *
 * The counts are the reason this screen is a list rather than a table of
 * titles: a posting with nine submitted and none selected is a different
 * problem from one with nobody submitted at all, and neither is visible from
 * the title. Submitted → selected → placed, left to right, so the stage that
 * has stalled is the one you notice.
 *
 * Closing is a button rather than an editable field. The server stamps the
 * date, so there is nothing here that can leave a posting closed on no
 * particular day.
 */
import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  Briefcase,
  ChevronDown,
  ChevronRight,
  Loader2,
  Plus,
  RotateCcw,
  XCircle,
} from 'lucide-react';
import { toast } from 'sonner';
import { client, errorMessage } from '../api/client';
import { money } from '../lib/money';
import { SHIFT_KINDS, composeWorkTime } from '../lib/workTime';
import { monthsFromYears } from '../lib/experience';
import { formatPhone } from '../lib/phone';
import { titleCaseName } from '../lib/titleCase';
import ClientSelect from '../components/ClientSelect';
import type {
  JobOpeningWithPipeline,
  Page,
  Submission,
} from '../types';

/** The band as one readable line, however much of it was filled in. */
/**
 * Skills offered in the box, as the specification asks.
 *
 * A datalist, not a dropdown. The field is free text and stays free text:
 * these are the ones typed often enough to be worth one keystroke, and a
 * closed list would refuse the requirement that does not fit it. Selecting one
 * fills the box; typing anything else is untouched.
 */
const SKILL_SUGGESTIONS = [
  'Sales',
  'Marketing',
  'Tele Sales',
  'Customer Support',
  'Field Sales',
  'Data Entry',
  'Accounts',
  'Nursing',
  'Pharmacist',
  'Phlebotomy',
  'Lab Technician',
  'Delivery',
  'Warehouse',
  'Housekeeping',
  'Security',
];

/**
 * Monthly salary figures offered on the two range boxes.
 *
 * The specification asks for 10,000 to 2,00,000 monthly. Offered through a
 * datalist rather than a closed dropdown for a reason that is not stylistic:
 * the 119 requirements already on file hold *mixed units* -- 14,000 beside
 * 20.00 beside 900,000 -- so the column has never meant one thing. A closed
 * dropdown capped at 200,000 would make the existing 900,000 row uneditable,
 * and a select cannot represent a value it was not given without changing it.
 *
 * Nothing is migrated and nothing is reinterpreted. The label now says
 * monthly, so what is typed from here on is unambiguous; what is already
 * stored is left exactly as it is.
 */
const MONTHLY_SALARY_STEPS = [
  10_000, 15_000, 20_000, 25_000, 30_000, 40_000, 50_000,
  60_000, 75_000, 100_000, 125_000, 150_000, 175_000, 200_000,
];

/** Shift patterns offered beside the hours. Composed into work_time. */


function salaryBand(opening: JobOpeningWithPipeline): string | null {
  const { salaryMin, salaryMax } = opening;
  if (salaryMin && salaryMax) return `${money(salaryMin)} – ${money(salaryMax)}`;
  if (salaryMin) return `from ${money(salaryMin)}`;
  if (salaryMax) return `up to ${money(salaryMax)}`;
  return null;
}

function experienceLabel(months: number | null): string | null {
  if (months === null) return null;
  if (months === 0) return 'Fresher';
  const years = Math.floor(months / 12);
  const rest = months % 12;
  if (years === 0) return `${rest}m+`;
  return rest === 0 ? `${years}y+` : `${years}y ${rest}m+`;
}

function Funnel({ opening }: { opening: JobOpeningWithPipeline }) {
  const stages = [
    { label: 'submitted', value: opening.submissionCount },
    { label: 'selected', value: opening.selectedCount },
    { label: 'placed', value: opening.placementCount },
  ];
  return (
    <div className="flex items-center gap-4">
      {stages.map((stage) => (
        <div key={stage.label} className="text-center">
          <p
            className={`text-sm font-semibold ${
              stage.value > 0 ? 'text-gray-900' : 'text-gray-300'
            }`}
          >
            {stage.value}
          </p>
          <p className="text-[10px] uppercase tracking-wide text-gray-400">
            {stage.label}
          </p>
        </div>
      ))}
    </div>
  );
}

function Applicants({ openingId }: { openingId: string }) {
  const applicants = useQuery<Page<Submission>>({
    queryKey: ['job-applicants', openingId],
    queryFn: async () =>
      (await client.get(`/api/v1/job-openings/${openingId}/submissions`, {
        params: { limit: 200 },
      })).data,
  });

  if (applicants.isLoading) {
    return (
      <div className="flex justify-center py-4 text-gray-400">
        <Loader2 className="h-4 w-4 animate-spin" />
      </div>
    );
  }
  if (applicants.isError) {
    return <p className="py-3 text-xs text-red-700">Could not load applicants.</p>;
  }

  const rows = applicants.data?.items ?? [];
  if (rows.length === 0) {
    return (
      <p className="py-3 text-xs text-gray-500">
        Nobody has been put forward for this posting yet.
      </p>
    );
  }

  return (
    <table className="mt-3 w-full text-xs">
      <thead>
        <tr className="text-left text-gray-400">
          <th className="pb-2 font-medium">Candidate</th>
          <th className="pb-2 font-medium">Submitted</th>
          <th className="pb-2 font-medium">Status</th>
        </tr>
      </thead>
      <tbody className="divide-y divide-gray-100">
        {rows.map((row) => (
          <tr key={row.id}>
            <td className="py-2 text-gray-900">
              {titleCaseName(row.candidateName) ?? row.candidateId}
              {row.candidatePhone && (
                <span className="ml-2 text-gray-400">{formatPhone(row.candidatePhone)}</span>
              )}
            </td>
            <td className="py-2 text-gray-500">{row.submittedOn}</td>
            <td className="py-2">
              <span className="rounded bg-gray-100 px-2 py-0.5 text-gray-600">
                {row.status.replace(/_/g, ' ')}
              </span>
            </td>
          </tr>
        ))}
      </tbody>
    </table>
  );
}

function PostForm({
  submitting,
  onSubmit,
}: {
  submitting: boolean;
  onSubmit: (body: Record<string, unknown>) => void;
}) {
  const [clientId, setClientId] = useState('');
  const [title, setTitle] = useState('');
  const [location, setLocation] = useState('');
  const [positionsOpen, setPositionsOpen] = useState('1');
  const [salaryMin, setSalaryMin] = useState('');
  const [salaryMax, setSalaryMax] = useState('');
  const [description, setDescription] = useState('');

  // The rest of the client's intake sheet. Kept behind a disclosure below,
  // because most postings are made from a phone call with four of these known
  // and the everyday path should not be a twenty-field form.
  const [clientAddress, setClientAddress] = useState('');
  const [minQualification, setMinQualification] = useState('');
  const [industryType, setIndustryType] = useState('');
  const [functionalArea, setFunctionalArea] = useState('');
  /**
   * The hours, and the shift pattern, composed into the one free-text column.
   *
   * work_time is a String(120) and is empty on all 119 requirements on file,
   * so there is nothing to parse back and nothing to migrate -- which is why
   * these can be structured inputs writing a sentence rather than a schema
   * change. What goes in reads "9:00 AM to 6:00 PM · Day shift".
   */
  const [workFrom, setWorkFrom] = useState('');
  const [workTo, setWorkTo] = useState('');
  const [shifts, setShifts] = useState<string[]>([]);
  const [skills, setSkills] = useState('');
  const [hiringProcess, setHiringProcess] = useState('');
  const [jdRemarks, setJdRemarks] = useState('');
  const [targetCloseOn, setTargetCloseOn] = useState('');
  // Years on the form, months in the column — the conversion happens at the
  // boundary through the shared helper, which is what makes this form and the
  // client page's agree about what a typed 3 means.
  const [minExperienceYears, setMinExperienceYears] = useState('');
  const [maxExperienceYears, setMaxExperienceYears] = useState('');

  const ready = clientId !== '' && title.trim() !== '';

  return (
    <form
      className="card space-y-3"
      onSubmit={(e) => {
        e.preventDefault();
        if (!ready) return;
        onSubmit({
          clientId,
          title: title.trim(),
          location: location.trim() || null,
          positionsOpen: Number(positionsOpen) || 1,
          salaryMin: salaryMin.trim() || null,
          salaryMax: salaryMax.trim() || null,
          description: description.trim() || null,
          clientAddress: clientAddress.trim() || null,
          minQualification: minQualification.trim() || null,
          industryType: industryType.trim() || null,
          functionalArea: functionalArea.trim() || null,
          workTime: composeWorkTime(workFrom, workTo, shifts),
          skills: skills.trim() || null,
          hiringProcess: hiringProcess.trim() || null,
          jdRemarks: jdRemarks.trim() || null,
          targetCloseOn: targetCloseOn || null,
          minExperienceMonths: monthsFromYears(minExperienceYears),
          maxExperienceMonths: monthsFromYears(maxExperienceYears),
        });
      }}
    >
      <h3 className="text-sm font-semibold text-gray-900">Post a Role</h3>

      <div className="grid gap-3 sm:grid-cols-2">
        <label className="text-xs text-gray-600">
          Client
          <ClientSelect
            purpose="work"
            className="input mt-1"
            value={clientId}
            onChange={setClientId}
            required
            placeholder="Select a client…"
          />
        </label>

        <label className="text-xs text-gray-600">
          Job Title
          <input
            spellCheck={false}
            className="input mt-1"
            value={title}
            onChange={(e) => setTitle(e.target.value)}
            placeholder="Golang Engineer — capitalise each word"
            required
          />
          <span className="mt-0.5 block text-[11px] text-gray-400">
            First letter of every word capitalised, as it should read on the
            posting.
          </span>
        </label>

        <label className="text-xs text-gray-600">
          Work Location
          <input
            spellCheck={false}
            className="input mt-1"
            value={location}
            onChange={(e) => setLocation(e.target.value)}
            placeholder="Hyderabad"
          />
        </label>

        <label className="text-xs text-gray-600">
          Headcount
          <input
            className="input mt-1"
            type="number"
            min={1}
            value={positionsOpen}
            onChange={(e) => setPositionsOpen(e.target.value)}
          />
        </label>

        {/* Offered through a datalist rather than a closed dropdown, and
            that is not a style choice: the 119 requirements on file hold mixed
            units -- 14,000 beside 20.00 beside 900,000 -- so a select capped
            at 200,000 would make the existing rows uneditable. Nothing is
            migrated or reinterpreted; the label says monthly so what is typed
            from here on is unambiguous. */}
        <datalist id="monthly-salary-steps">
          {MONTHLY_SALARY_STEPS.map((n) => (
            <option key={n} value={n} />
          ))}
        </datalist>

        <label className="text-xs text-gray-600">
          Monthly Salary From
          <input
            spellCheck={false}
            className="input mt-1"
            inputMode="decimal"
            list="monthly-salary-steps"
            value={salaryMin}
            onChange={(e) => setSalaryMin(e.target.value)}
            placeholder="10000"
          />
        </label>

        <label className="text-xs text-gray-600">
          Monthly Salary To
          <input
            spellCheck={false}
            className="input mt-1"
            inputMode="decimal"
            list="monthly-salary-steps"
            value={salaryMax}
            onChange={(e) => setSalaryMax(e.target.value)}
            placeholder="200000"
          />
          <span className="mt-0.5 block text-[11px] text-gray-400">
            Monthly. Older requirements were recorded in mixed units and are
            left as they were.
          </span>
        </label>
      </div>

      <label className="block text-xs text-gray-600">
        Roles &amp; Responsibilities
        <textarea
          spellCheck={true}
          className="input mt-1"
          rows={3}
          value={description}
          onChange={(e) => setDescription(e.target.value)}
          placeholder="What the client is looking for."
        />
      </label>

      {/* The rest of the intake sheet, behind a disclosure.
          Most postings are made off a phone call with a title, a location and
          a headcount known; putting twenty boxes in front of that turns a
          two-minute job into a form to get through. Closed by default and
          everything inside optional. */}
      <details className="rounded-lg border border-gray-200 px-3 py-2">
        <summary className="cursor-pointer text-xs font-medium text-gray-600">
          Full Requirement Details
        </summary>

        <div className="mt-3 space-y-3">
          <div className="grid gap-3 sm:grid-cols-2">
            <label className="text-xs text-gray-600">
              Client Address
              <input
                spellCheck={false}
                className="input mt-1"
                maxLength={255}
                value={clientAddress}
                onChange={(e) => setClientAddress(e.target.value)}
                placeholder="Where the client sits, if not the work location"
              />
            </label>

            <label className="text-xs text-gray-600">
              Industry
              <input
                spellCheck={false}
                className="input mt-1"
                maxLength={120}
                value={industryType}
                onChange={(e) => setIndustryType(e.target.value)}
                placeholder="Healthcare"
              />
            </label>

            <label className="text-xs text-gray-600">
              Functional Area
              <input
                spellCheck={false}
                className="input mt-1"
                maxLength={120}
                value={functionalArea}
                onChange={(e) => setFunctionalArea(e.target.value)}
                placeholder="Laboratory"
              />
            </label>

            <label className="text-xs text-gray-600">
              Min. Education/Qualification
              <input
                spellCheck={false}
                className="input mt-1"
                maxLength={160}
                value={minQualification}
                onChange={(e) => setMinQualification(e.target.value)}
                placeholder="B.Sc"
              />
            </label>

            <div className="text-xs text-gray-600 sm:col-span-2">
              Job Timings
              <div className="mt-1 flex flex-wrap items-center gap-2">
                <input
                  className="input w-auto"
                  type="time"
                  aria-label="Work time from"
                  value={workFrom}
                  onChange={(e) => setWorkFrom(e.target.value)}
                />
                <span className="text-gray-400">to</span>
                <input
                  className="input w-auto"
                  type="time"
                  aria-label="Work time to"
                  value={workTo}
                  onChange={(e) => setWorkTo(e.target.value)}
                />
                {SHIFT_KINDS.map((kind) => (
                  <label
                    key={kind}
                    className="flex items-center gap-1.5 whitespace-nowrap text-xs text-gray-600"
                  >
                    <input
                      type="checkbox"
                      checked={shifts.includes(kind)}
                      onChange={(event) =>
                        setShifts(
                          event.target.checked
                            ? [...shifts, kind]
                            : shifts.filter((s) => s !== kind),
                        )
                      }
                    />
                    {kind}
                  </label>
                ))}
              </div>
              <span className="mt-0.5 block text-[11px] text-gray-400">
                {composeWorkTime(workFrom, workTo, shifts)
                  ?? 'Nothing recorded yet.'}
              </span>
            </div>

            {/* Years, not months, and that is a bug fix rather than a
                relabelling. This form asked for months while the client
                page's requirement form asked for years, and both wrote
                the same column -- so a typed 3 meant three years on one
                screen and three months on the other, with nothing on
                either saying which. Years is the one that wins because
                that is how clients and recruiters state a band. */}
            <label className="text-xs text-gray-600">
              Experience From (Years)
              <input
                className="input mt-1"
                type="number"
                min={0}
                step="0.5"
                value={minExperienceYears}
                onChange={(e) => setMinExperienceYears(e.target.value)}
              />
            </label>

            <label className="text-xs text-gray-600">
              Experience To (Years)
              <input
                className="input mt-1"
                type="number"
                min={0}
                step="0.5"
                value={maxExperienceYears}
                onChange={(e) => setMaxExperienceYears(e.target.value)}
              />
            </label>

            <label className="text-xs text-gray-600">
              Requirements Closing Date
              <input
                className="input mt-1"
                type="date"
                value={targetCloseOn}
                onChange={(e) => setTargetCloseOn(e.target.value)}
              />
              <span className="mt-0.5 block text-[11px] text-gray-400">
                The client&rsquo;s turnaround. The date it arrived is recorded
                on its own.
              </span>
            </label>
          </div>

          {/* The suggested set sits in the box, as the specification asks.
              Chips rather than a dropdown, because the field is a list and a
              select can only put one thing in it -- clicking appends, and
              anything typed by hand is untouched. */}
          <label className="block text-xs text-gray-600">
            Required 5 Imp Skills
            <textarea
              spellCheck={true}
              className="input mt-1"
              rows={2}
              maxLength={2000}
              value={skills}
              onChange={(e) => setSkills(e.target.value)}
              placeholder="Venipuncture, sample handling"
            />
            <span className="mt-1 flex flex-wrap gap-1">
              {SKILL_SUGGESTIONS.filter(
                (s) => !skills.toLowerCase().includes(s.toLowerCase()),
              ).map((s) => (
                <button
                  key={s}
                  type="button"
                  className="rounded-full border border-gray-200 px-2 py-0.5 text-[11px] text-gray-600 transition-colors hover:border-indigo-300 hover:bg-indigo-50 hover:text-indigo-700"
                  onClick={() =>
                    setSkills(skills.trim() ? `${skills.trim()}, ${s}` : s)
                  }
                >
                  {s}
                </button>
              ))}
            </span>
          </label>

          <label className="block text-xs text-gray-600">
            Interview Mode/Hiring Process
            <textarea
              spellCheck={true}
              className="input mt-1"
              rows={2}
              maxLength={2000}
              value={hiringProcess}
              onChange={(e) => setHiringProcess(e.target.value)}
              placeholder="One telephonic round, then in person"
            />
          </label>

          <label className="block text-xs text-gray-600">
            The Complete Job Description
            <textarea
              spellCheck={true}
              className="input mt-1"
              rows={2}
              maxLength={2000}
              value={jdRemarks}
              onChange={(e) => setJdRemarks(e.target.value)}
              placeholder="Anything about the requirement itself, in the client's words."
            />
            <span className="mt-0.5 block text-[11px] text-gray-400">
              {/* The supported home for a constraint the product deliberately
                  does not model as a field. Said here so somebody meets it at
                  the moment they would otherwise go looking for a dropdown. */}
              A note about the requirement, not the description itself. If a
              client states a preference the product has no field for, record
              their wording here.
            </span>
          </label>
        </div>
      </details>

      <div className="flex justify-end">
        <button type="submit" className="btn-primary" disabled={!ready || submitting}>
          {submitting ? <Loader2 className="h-4 w-4 animate-spin" /> : null}
          Post role
        </button>
      </div>
    </form>
  );
}

export default function JobsPage() {
  const queryClient = useQueryClient();
  const [showForm, setShowForm] = useState(false);
  const [openOnly, setOpenOnly] = useState(true);
  /**
   * Narrow to the requirements this person is sourcing for.
   *
   * Matched on the assigned recruiter, not on who typed the posting in --
   * a coordinator entering a client's requirement is not the person working
   * it, and this page is about the work.
   *
   * Unlike the other lists, requirements carry no owner scope at all: every
   * one of them belongs to the company and anybody doing recruitment sees all
   * of them. That is deliberate and unchanged, which makes this the one place
   * the toggle does something for a recruiter as well as for a supervisor.
   */
  const [mineOnly, setMineOnly] = useState(false);
  const [search, setSearch] = useState('');
  const [expandedId, setExpandedId] = useState<string | null>(null);

  const openings = useQuery<Page<JobOpeningWithPipeline>>({
    queryKey: ['job-openings', openOnly, mineOnly, search],
    queryFn: async () =>
      (await client.get('/api/v1/job-openings', {
        params: {
          limit: 200,
          ...(openOnly ? { openOnly: true } : {}),
          ...(mineOnly ? { mineOnly: true } : {}),
          ...(search.trim() ? { search: search.trim() } : {}),
        },
      })).data,
  });


  const invalidate = () => {
    queryClient.invalidateQueries({ queryKey: ['job-openings'] });
  };

  const post = useMutation({
    mutationFn: async (body: Record<string, unknown>) =>
      (await client.post('/api/v1/job-openings', body)).data,
    onSuccess: () => {
      invalidate();
      setShowForm(false);
      toast.success('Role posted');
    },
    onError: (e) => toast.error(errorMessage(e)),
  });

  const setOpenState = useMutation({
    mutationFn: async ({ id, action }: { id: string; action: 'close' | 'reopen' }) =>
      (await client.post(`/api/v1/job-openings/${id}/${action}`)).data,
    onSuccess: (_data, variables) => {
      invalidate();
      toast.success(variables.action === 'close' ? 'Posting closed' : 'Posting reopened');
    },
    onError: (e) => toast.error(errorMessage(e)),
  });

  const rows = openings.data?.items ?? [];

  return (
    <div className="space-y-6">
      <section className="card">
        <div className="flex flex-wrap items-start justify-between gap-3">
          <div>
            <h2 className="text-sm font-semibold text-gray-900">Job Postings</h2>
            <p className="mt-1 max-w-2xl text-xs text-gray-500">
              What each client is hiring for, and how far the candidates put
              forward have got. A posting with people submitted but none
              selected is stuck in a different place from one nobody has been
              submitted to.
            </p>
          </div>
          <button
            type="button"
            className="btn-secondary"
            onClick={() => setShowForm((v) => !v)}
          >
            <Plus className="h-4 w-4" />
            {showForm ? 'Cancel' : 'Post a role'}
          </button>
        </div>

        <div className="mt-3 flex flex-wrap items-center gap-4">
          <label className="flex items-center gap-2 text-xs text-gray-600">
            <input
              type="checkbox"
              className="rounded border-gray-300"
              checked={openOnly}
              onChange={(e) => setOpenOnly(e.target.checked)}
            />
            Open only
          </label>
          <label className="flex items-center gap-2 text-xs text-gray-600">
            <input
              type="checkbox"
              className="rounded border-gray-300"
              checked={mineOnly}
              onChange={(e) => setMineOnly(e.target.checked)}
            />
            Only mine
          </label>
          <input
            spellCheck={false}
            className="input max-w-xs text-xs"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Search titles…"
          />
        </div>
      </section>

      {showForm && (
        <PostForm
          submitting={post.isPending}
          onSubmit={post.mutate}
        />
      )}

      {openings.isLoading ? (
        <div className="flex justify-center py-10 text-gray-400">
          <Loader2 className="h-5 w-5 animate-spin" />
        </div>
      ) : openings.isError ? (
        <div className="card flex items-center justify-between border border-red-200 bg-red-50">
          <p className="text-sm text-red-800">Could not load postings.</p>
          <button type="button" className="btn-ghost" onClick={() => void openings.refetch()}>
            Retry
          </button>
        </div>
      ) : rows.length === 0 ? (
        <div className="card flex items-center gap-3 text-sm text-gray-500">
          <Briefcase className="h-4 w-4 text-gray-400" />
          {openOnly
            ? 'No open postings. Uncheck “Open only” to see closed ones.'
            : 'No postings yet.'}
        </div>
      ) : (
        <div className="space-y-3">
          {rows.map((opening) => {
            const expanded = expandedId === opening.id;
            const band = salaryBand(opening);
            const experience = experienceLabel(opening.minExperienceMonths);
            return (
              <section key={opening.id} className="card">
                <div className="flex flex-wrap items-start justify-between gap-4">
                  <div className="min-w-0">
                    <div className="flex items-center gap-2">
                      <h3 className="text-sm font-semibold text-gray-900">
                        {opening.title}
                      </h3>
                      {!opening.isOpen && (
                        <span className="rounded bg-gray-200 px-2 py-0.5 text-[10px] uppercase tracking-wide text-gray-600">
                          closed
                        </span>
                      )}
                    </div>
                    <p className="mt-1 text-xs text-gray-500">
                      {opening.clientName ?? 'Unknown client'}
                      {opening.location ? ` · ${opening.location}` : ''}
                      {opening.positionsOpen > 1 ? ` · ${opening.positionsOpen} positions` : ''}
                    </p>
                    {(band || experience) && (
                      <p className="mt-1 text-xs text-gray-400">
                        {[band, experience].filter(Boolean).join(' · ')}
                      </p>
                    )}
                  </div>

                  <div className="flex items-center gap-5">
                    <Funnel opening={opening} />
                    {opening.isOpen ? (
                      <button
                        type="button"
                        className="btn-ghost text-xs"
                        disabled={setOpenState.isPending}
                        onClick={() =>
                          setOpenState.mutate({ id: opening.id, action: 'close' })
                        }
                      >
                        <XCircle className="h-4 w-4" />
                        Close
                      </button>
                    ) : (
                      <button
                        type="button"
                        className="btn-ghost text-xs"
                        disabled={setOpenState.isPending}
                        onClick={() =>
                          setOpenState.mutate({ id: opening.id, action: 'reopen' })
                        }
                      >
                        <RotateCcw className="h-4 w-4" />
                        Reopen
                      </button>
                    )}
                  </div>
                </div>

                <button
                  type="button"
                  className="mt-3 flex items-center gap-1 text-xs text-gray-500 hover:text-gray-900"
                  onClick={() => setExpandedId(expanded ? null : opening.id)}
                >
                  {expanded ? (
                    <ChevronDown className="h-3.5 w-3.5" />
                  ) : (
                    <ChevronRight className="h-3.5 w-3.5" />
                  )}
                  {opening.submissionCount === 1
                    ? '1 candidate submitted'
                    : `${opening.submissionCount} candidates submitted`}
                </button>

                {expanded && <Applicants openingId={opening.id} />}
              </section>
            );
          })}
        </div>
      )}
    </div>
  );
}
